<template>
    <div v-if="!loading && subscriptions" class="w-full flex flex-column gap-3">
        <div class="flex gap-2">
            <Button label="Экспорт" icon="pi pi-download" @click="exportCSV" severity="secondary" size="small" class="flex"/>
            <Button label="Обновить" icon="pi pi-refresh" @click="loadEvents" severity="secondary" size="small" class="flex"/>
            <Button label="Сбросить" icon="pi pi-times" @click="resetFilters" severity="secondary" size="small" class="flex"/>
        </div>

        <DataTable
            ref="dt"
            :value="subscriptions"
            responsiveLayout="scroll"
            v-model:filters="lazyParams.filters"
            @page="onPage($event)"
            @sort="onSort($event)"
            @filter="onFilter($event)"
            :globalFilterFields="globalFields"
            lazy
            filterDisplay="menu"
            :rows="lazyParams.rows"
            :totalRecords="totalSubscriptions"
            :loading="loading"
        >
            <template #header>
                <div class="text-sm flex flex-row gap-2 text-600 font-medium flex-wrap">
                    <span class="flex my-auto py-2">Итого: {{ totalSubscriptions }}</span>
                </div>
            </template>

            <Column field="id" sortable filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">ID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 5rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.id }}
                            </span>
                        </span>
                        <Button @click="copy(data.id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="subscription_id" sortable filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Подписка</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 5rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.subscription_id }}
                            </span>
                        </span>
                        <Button @click="copy(data.subscription_id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="event_type" sortable filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Тип события</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <Tag :value="data.event_type || '-'" class="text-xs" severity="info" />
                </template>
            </Column>

            <Column field="payload_json" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Payload</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div v-if="data.payload_json" class="text-sm flex flex-row gap-1 align-items-center">
                        <Button @click="openPayloadModal(data.payload_json)" icon="pi pi-eye" iconPos="right" text severity="info" class="text-xs" size="small"/>
                        <Button @click="copy(formatPayload(data.payload_json))" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                    <span v-else class="text-xs">-</span>
                </template>
            </Column>

            <Column field="created_at" filterField="created_at" sortable dataType="date" :showFilterMatchModes="false" :showFilterOperator="false">
                <template #header>
                    <span class="text-xs font-medium">Дата создания</span>
                </template>
                <template #filter="{ filterModel }">
                    <DatePicker v-model="filterModel.value" dateFormat="dd.mm.yy" placeholder="dd.mm.yy" mask="99.99.9999" showTime hourFormat="24" fluid/>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ formatNullableDate(data.created_at) }}</span>
                </template>
            </Column>

            <template #footer>
                <div class="text-sm flex flex-row gap-2 text-600 font-medium flex-wrap">
                    <span class="flex my-auto py-2">Итого: {{ totalSubscriptions }}</span>
                </div>
            </template>
        </DataTable>

        <Paginator
            v-model:first="lazyParams.first"
            :rows="lazyParams.rows"
            :totalRecords="totalSubscriptions"
            template="FirstPageLink  PageLinks LastPageLink RowsPerPageDropdown"
            :rowsPerPageOptions="[10, 20, 30, 50, 100, 200, 300, 400, 500, 1000, 2000, 3000, 4000, 5000]"
            @page="onPage"
        />

        <Dialog v-model:visible="payloadDialog.visible" modal header="Payload" :style="{ width: 'min(900px, 95vw)' }">
            <div class="w-full overflow-auto">
                <pre class="text-sm m-0 white-space-pre-wrap line-height-3">{{ payloadDialog.content }}</pre>
            </div>
        </Dialog>
    </div>
</template>

<script>
import Helper from '@/assets/js/helper.js'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'

export default {
    props: {
        subscriptions: {
            type: Array,
            required: true
        },
        users: {
            type: Array,
            default: () => []
        },
        totalSubscriptions: {
            type: Number,
            default: 0
        },
        loading: {
            type: Boolean,
            default: false
        },
        defaultParams: {
            type: Object,
            default: () => ({})
        },
        filters: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['filter', 'lazyParams'],
    data() {
        return {
            lazyParams: this.defaultParams,
            payloadDialog: {
                visible: false,
                content: ''
            },
            sort: {
                field: 'created_at',
                order: -1
            },
            globalFields: ['id', 'subscription_id', 'event_type', 'payload_json', 'created_at'],
            dt: null
        }
    },
    methods: {
        resetFilters() {
            this.lazyParams.filters = {
                id: { value: '', matchMode: 'contains' },
                subscription_id: { value: '', matchMode: 'contains' },
                event_type: { value: '', matchMode: 'contains' },
                payload_json: { value: '', matchMode: 'contains' },
                created_at: {
                    operator: FilterOperator.AND,
                    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
                }
            }
            this.$emit('filter', this.lazyParams.filters)
        },
        formatDate(dateStr) {
            return this.helper.formatDate(dateStr)
        },
        formatNullableDate(dateStr) {
            if (!dateStr) return '-'
            return this.formatDate(dateStr)
        },
        formatPayload(payload) {
            if (!payload) return ''
            if (typeof payload === 'string') return payload
            try {
                return JSON.stringify(payload)
            } catch (error) {
                return String(payload)
            }
        },
        formatPayloadPretty(payload) {
            if (!payload) return ''
            if (typeof payload === 'string') {
                try {
                    return JSON.stringify(JSON.parse(payload), null, 2)
                } catch (error) {
                    return payload
                }
            }
            try {
                return JSON.stringify(payload, null, 2)
            } catch (error) {
                return String(payload)
            }
        },
        openPayloadModal(payload) {
            this.payloadDialog.content = this.formatPayloadPretty(payload)
            this.payloadDialog.visible = true
        },
        copy(text) {
            if (text === null || text === undefined || text === '') return
            const value = typeof text === 'string' ? text : String(text)
            this.helper.copy_clipboard(value)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: value, life: 1000 })
        },
        onPage(event) {
            this.lazyParams.first = event.first
            this.lazyParams.rows = event.rows
            this.lazyParams.page = event.page
            this.lazyParams.pageCount = event.pageCount
            this.$emit('lazyParams', this.lazyParams)
        },
        onSort(event) {
            if (event.sortField && event.sortOrder) {
                this.sort = {
                    field: event.sortField,
                    order: event.sortOrder
                }
            } else {
                this.sort = {
                    field: 'created_at',
                    order: -1
                }
            }
            this.lazyParams.sort = this.sort
            this.$emit('lazyParams', this.lazyParams)
        },
        onFilter(params) {
            this.lazyParams.filters = params.filters
            this.$emit('filter', params.filters)
        },
        loadEvents() {
            this.$emit('lazyParams', this.lazyParams)
        },
        exportCSV() {
            this.$refs.dt.exportCSV()
        }
    },
    async beforeCreate() {
        this.helper = new Helper()
    },
    mounted() {
        this.dt = this.$refs.dt
    }
}
</script>
